import React from 'react';
import PropTypes from 'prop-types';

const BOLD_TAG_NAME = 'B';
const DELIMITER = '|';
const DOM_PARSER_TYPE = 'text/html';

/**
 * Renders the provided text, with bold tags around any text matching the provided "textToBold".
 *
 * @example
 * const text = 'Primary Care';
 * const textToBold = 'car';
 * <BoldMatchedText text={text} textToBold={textToBold} />
 *
 * Should render:
 * <span>Primary </span><b>Car</b><span>e</span>
 */
const BoldMatchedText = ({ text, textToBold, caseInsensitive }) => {
  if (!textToBold) {
    return text;
  }

  const flags = caseInsensitive ? 'gi' : 'g';
  const regExp = new RegExp(textToBold, flags);
  const delimitedText = text.replaceAll(regExp, (match) => `${DELIMITER}<${BOLD_TAG_NAME}>${match}</${BOLD_TAG_NAME}>${DELIMITER}`);
  const parts = delimitedText.split(DELIMITER);

  const domParser = new DOMParser();
  let jsx = [];

  for (const [index, part] of parts.entries()) {
    const parsed = domParser.parseFromString(part, DOM_PARSER_TYPE);
    if (!parsed.body.firstChild) {
      continue;
    }

    if (parsed.body.firstChild.tagName === BOLD_TAG_NAME) {
      jsx.push(<b key={index}>{parsed.body.firstChild.innerHTML}</b>);
    }
    else {
      jsx.push(<span key={index}>{part}</span>);
    }
  }

  return jsx;
}

BoldMatchedText.propTypes = {
  /**
   * the text to display
   */
  text: PropTypes.string.isRequired,
  /**
   * the text to bold
   */
  textToBold: PropTypes.string.isRequired,
  /**
   * whether or not to use a case insensitive match
   */
  caseInsensitive: PropTypes.bool,
};

BoldMatchedText.defaultProps = {
  caseInsensitive: true,
};

export default BoldMatchedText;
